import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  ErrorMessage,
  Field,
  Input,
  Label,
} from '@ankor-io/ui-components'

import { useBackfillConnectedAccountCardMutation, useConnectedAccountCardCheckQuery } from '@/queries'

const formSchema = z.object({
  cardId: z.string().trim().min(1, 'Card ID is required'),
})

type FormData = z.infer<typeof formSchema>

const SystemStatusRow = ({ label, exists, detail }: { label: string; exists: boolean; detail?: string }) => (
  <div className="flex items-center gap-2">
    <span className={`inline-block h-2.5 w-2.5 rounded-full ${exists ? 'bg-green-500' : 'bg-gray-300'}`} />
    <span className="font-medium">{label}</span>
    <span className={exists ? 'text-green-700' : 'text-gray-500'}>{exists ? 'Found' : 'Not found'}</span>
    {exists && detail && <span className="text-gray-400">({detail})</span>}
  </div>
)

const getCheckErrorMessage = (error: Error): string => {
  return `Something went wrong while checking the card. Please try again. Error details: ${error.name}: ${error.message}`
}

export default function ConnectedAccountCardChecker({
  connectedAccountId,
  orgId,
  vesselId,
}: {
  connectedAccountId: string
  orgId: string
  vesselId: string
}) {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  })

  const [checkedCardId, setCheckedCardId] = useState('')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const { data, isFetching, isError, error } = useConnectedAccountCardCheckQuery(
    orgId,
    vesselId,
    connectedAccountId,
    checkedCardId,
    { enabled: checkedCardId !== '' },
  )

  const backfill = useBackfillConnectedAccountCardMutation()

  // The check endpoint returns a tuple: [card in AnkorPay's database, card in Airwallex].
  // Either entry is null when the card is not present in that system.
  const [ankorCard, airwallexCard] = data ?? [null, null]
  const hasResult = !isFetching && !isError && data != null
  const notInDatabase = hasResult && ankorCard == null

  const onSubmit = (formData: FormData) => {
    setCheckedCardId(formData.cardId)
  }

  const handleBackfill = () => {
    backfill.mutate(
      { orgId, vesselId, connectedAccountId, cardId: checkedCardId },
      { onSuccess: () => setIsConfirmOpen(false) },
    )
  }

  return (
    <div className="border-1 border-gray-200 rounded-xl p-4">
      <p className="font-bold">Check card</p>
      <p>Check if a card exists in AnkorPay database</p>

      <form
        className="mt-2"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Field>
          <Label htmlFor="cardId">Card ID</Label>
          <Input
            id="cardId"
            {...form.register('cardId')}
          />
          <ErrorMessage>{form.formState.errors.cardId?.message}</ErrorMessage>
        </Field>
        <Button type="submit">Check</Button>
      </form>

      {checkedCardId !== '' && (
        <div className="mt-3 text-sm">
          {isFetching && <p>Checking…</p>}
          {isError && <p className="text-red-600">{getCheckErrorMessage(error)}</p>}
          {hasResult && (
            <div className="space-y-1.5">
              <SystemStatusRow
                label="Airwallex"
                exists={airwallexCard != null}
                detail={airwallexCard?.card_number}
              />
              <SystemStatusRow
                label="AnkorPay database"
                exists={ankorCard != null}
                detail={ankorCard?.maskedCardNumber}
              />

              {notInDatabase && (
                <Button
                  color="primary"
                  className="mt-2"
                  onClick={() => setIsConfirmOpen(true)}
                >
                  Backfill to AnkorPay
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        size="md"
      >
        <DialogTitle>Backfill card</DialogTitle>
        <DialogDescription>
          This will create a record for card <span className="font-medium">{checkedCardId}</span> in the AnkorPay
          database.
        </DialogDescription>
        <DialogBody>
          {backfill.isError && <p className="text-sm text-red-600">Backfill failed. Please try again.</p>}
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setIsConfirmOpen(false)}
            disabled={backfill.isPending}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleBackfill}
            disabled={backfill.isPending}
          >
            {backfill.isPending ? 'Backfilling…' : 'Backfill'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
